import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PriceAlert, PriceAlertInput, PriceAlertPatch, PriceQuote } from '@pipyar/core';
import { api } from '@/lib/api';

const ALERTS_KEY = ['price-alerts'] as const;

/**
 * Fallback polling for the price.
 *
 * The selected pair's price is PUSHED by the live stream (see LiveStream),
 * which writes straight into this query's cache several times a second. The
 * poll only matters when the stream is down, and for alerts on a pair that is
 * not the one selected.
 */
export const QUOTE_POLL_MS = 5_000;

export const quoteKey = (symbol: string) => ['alert-quote', symbol] as const;

export function useAlerts() {
  return useQuery({
    queryKey: ALERTS_KEY,
    queryFn: () => api.get<{ alerts: PriceAlert[] }>('/api/alerts'),
    select: (d) => d.alerts,
    // Picks up a line moved, fired or stopped from another device. Polled in
    // the background too: the quotes are, so without this a backgrounded tab
    // would keep ringing for an alert already stopped elsewhere.
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

function quoteOptions(symbol: string) {
  return {
    queryKey: quoteKey(symbol),
    queryFn: () => api.get<PriceQuote>(`/api/alerts/quote?symbol=${encodeURIComponent(symbol)}`),
    refetchInterval: QUOTE_POLL_MS,
    // Keep checking while the tab is in the background — that is when an
    // alert is most useful.
    refetchIntervalInBackground: true,
    staleTime: QUOTE_POLL_MS - 500,
  };
}

export function useQuote(symbol: string | undefined) {
  return useQuery({ ...quoteOptions(symbol ?? ''), enabled: Boolean(symbol) });
}

/** One quote per symbol, shared by key with the page's own `useQuote`. */
export function useQuotes(symbols: string[]) {
  return useQueries({ queries: symbols.map((s) => quoteOptions(s)) });
}

/** Write one alert into the cached list, so the line does not snap back while saving. */
function patchCache(
  qc: ReturnType<typeof useQueryClient>,
  update: (alerts: PriceAlert[]) => PriceAlert[],
) {
  qc.setQueryData<{ alerts: PriceAlert[] }>(ALERTS_KEY, (prev) =>
    prev ? { alerts: update(prev.alerts) } : prev,
  );
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PriceAlertInput) =>
      api.post<{ alert: PriceAlert }>('/api/alerts', input).then((r) => r.alert),
    onSuccess: (alert) => patchCache(qc, (all) => [...all, alert]),
    onSettled: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: PriceAlertPatch }) =>
      api.patch<{ alert: PriceAlert }>(`/api/alerts/${id}`, patch).then((r) => r.alert),
    onMutate: ({ id, patch }) =>
      patchCache(qc, (all) =>
        all.map((a) =>
          a.id === id
            ? {
                ...a,
                ...(patch.price !== undefined ? { price: patch.price } : {}),
                ...(patch.direction !== undefined ? { direction: patch.direction } : {}),
                ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
                ...(patch.note !== undefined ? { note: patch.note } : {}),
              }
            : a,
        ),
      ),
    onSuccess: (alert) => patchCache(qc, (all) => all.map((a) => (a.id === alert.id ? alert : a))),
    onSettled: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/alerts/${id}`),
    onMutate: (id) => patchCache(qc, (all) => all.filter((a) => a.id !== id)),
    onSettled: () => qc.invalidateQueries({ queryKey: ALERTS_KEY }),
  });
}

/** Claim a crossing. Resolves true only for the client that should make the noise. */
export async function claimAlert(id: number, price: number): Promise<{ fired: boolean; alert: PriceAlert }> {
  return api.post<{ fired: boolean; alert: PriceAlert }>(`/api/alerts/${id}/trigger`, { price });
}

export { ALERTS_KEY };

/** Stop a fired alert's alarm on every device (until the line is re-armed). */
export async function ackAlert(id: number): Promise<PriceAlert> {
  return api.post<{ alert: PriceAlert }>(`/api/alerts/${id}/ack`).then((r) => r.alert);
}
